// @flow
import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from '@finect/tabular-icons/link';
import { FinectIsoPlus } from '@finect/tabular-components/FinectIsoPlus';
import { PlusLogo } from '@finect/tabular-components/PlusLogo';
import { DateLabel } from '@finect/tabular-components/DateLabel';
import { AppLink } from '@finect/tabular-core/AppLink';
import { Video } from '@finect/tabular-components/Videos';
import { getArticleRel } from '@finect/tabular-components/Articles';
import { EntityCard } from '@finect/tabular-components/EntityCard';
import { Image } from '@finect/tabular-components/Image';
import { Flex } from '../components/Flex';
import { Topic, Topics } from '../components/Topic';
import { Title6 } from '../components/Headings';
import { getArticlePreviewText } from '../../../utils/articles';
import { SITE_TIME_ZONE } from '../../../utils/siteTimeZone';
import {
  VideoContainer,
  VideoContent,
  ArticleContent,
  ArticleInfo,
  Small,
  External,
  // ExternalImage,
  SquareImage,
  ArticleCardContainer
} from '../partials';
import { getCommonProps } from './utils';

const renderTags = (tags, userIsPlus, actions) => {
  // plus tags first
  const plusTags = tags.filter(tag => tag.id >= 50100 && tag.id <= 50110);
  const otherTags = tags.filter(tag => !(tag.id >= 50100 && tag.id <= 50110));

  return [...plusTags, ...otherTags].map((tag) => {
    const plus2 = tag.id >= 50100 && tag.id <= 50110;

    if (plus2) {
      const props = userIsPlus
        ? { href: `/plus/analisis`, rel: 'nofollow' }
        : {
          as: 'button',
          onClick: (e) => {
            e.preventDefault();
            actions && actions.setOpenPlusModal(true);
          }
        };

      return (
        <Topic title={'Contenido Plus'} plus2={plus2} key={tag.id} {...props}>
          <FinectIsoPlus color="#fff" size={14} />
          <PlusLogo color="#fff" size={26} />
        </Topic>
      );
    }

    return (
      <EntityCard type="tag" entity={tag} key={tag.id}>
        <Topic
          href={`/temas/${tag.alias ? tag.alias : tag.url}`}
          title={tag.name}
          followed={tag.followed}
          plus2={plus2}
        >
          {tag.name}
        </Topic>
      </EntityCard>
    );
  });
};

type Props = {
  simple?: boolean,
  article: Object,
  onSubscribe: Function,
  userSubscriptions: Object,
  userLikes: Object,
  withTags?: boolean,
  withAuthor?: boolean,
  withText?: boolean,
  withDate?: boolean,
  disableMoveImage?: boolean
};

export const ArticleListCard = ({
  simple = false,
  article,
  userSubscriptions = {},
  withTags = true,
  withAuthor = true,
  withText = false,
  withDate = true,
  disableMoveImage = false
}: Props) => {
  const dispatch = useDispatch();
  const setOpenPlusModal = open => dispatch({ type: 'SET_PLUS_MODAL', open });

  const user = useSelector(state => state.user.user);
  const isPlusProvisional = user?.plus;

  const { title, date, externalUrl, externalPath, link, image, author, authorContent, tags, linkProps, video } =
    getCommonProps(article, userSubscriptions);

  const articleIsPlus = article.socialStats.tags?.some(tag => tag.id >= 50100 && tag.id <= 50110);
  const { plus: userIsPlus } = user;

  const showText = withText && (articleIsPlus ? userIsPlus : true) && article.body.text;

  if (['video'].includes(article.type) && video) {
    return (
      <ArticleCardContainer simple={simple}>
        <VideoContainer>
          {withTags && tags?.length > 0 && (
            <div>{<Topics>{renderTags(tags, isPlusProvisional, { setOpenPlusModal })}</Topics>}</div>
          )}
          <div>
            <VideoContent>
              {/* La miniatura enlaza a la página del artículo (no abre modal):
                  más visitas a una URL indexable. El modal se reserva al módulo
                  Multimedia de la Home. */}
              <Video video={video} href={link} rel={getArticleRel(article)} title={title} />
            </VideoContent>
            <div>
              <Title6 as="h3">
                <AppLink black href={link} title={title} rel={getArticleRel(article)}>
                  {title}
                </AppLink>
              </Title6>
              {showText && <p>{getArticlePreviewText(article.body.text)}</p>}

              <Flex align="center">
                {author && withAuthor && authorContent}
                <div>
                  <Small withLeftMargin={Boolean(author && withAuthor)}>
                    <DateLabel date={new Date(date)} format="LLL" timeZone={SITE_TIME_ZONE} />
                  </Small>
                </div>
              </Flex>
            </div>
          </div>
        </VideoContainer>
      </ArticleCardContainer>
    );
  }

  return (
    <ArticleCardContainer simple={simple}>
      <ArticleContent>
        <ArticleInfo hasImage={!simple && Boolean(image)}>
          <div>
            {!simple && withTags && tags?.length > 0 && (
              <div>{<Topics>{renderTags(tags, isPlusProvisional, { setOpenPlusModal })}</Topics>}</div>
            )}

            <div className="title-image">
              <Title6 as="h3">
                <a href={article.type !== 'podcast' && externalUrl ? externalUrl : link} title={title} {...linkProps}>
                  {title}
                </a>
              </Title6>

              {!simple && image && (
                <SquareImage
                  as="a"
                  href={externalUrl || link}
                  title={title}
                  {...linkProps}
                  disableMoveImage={Boolean(disableMoveImage)}
                  size="100px"
                  margin="0 0 12px 15px"
                >
                  <Image alt={title} src={image} width={100} height={100} />
                </SquareImage>
              )}
            </div>
          </div>
          <div>
            {showText && article.body.text && <p>{getArticlePreviewText(article.body.text, simple ? 160 : 280)}</p>}

            <Flex align="center">
              {!simple && author && withAuthor && authorContent}
              <div className="date-external">
                {externalUrl && (
                  <small>
                    <External href={externalUrl} rel="nofollow" target="_blank">
                      <Link size="16" strokeWidth="2.2" />
                      <span>{externalPath ? externalPath[0] : ''}</span>
                    </External>
                  </small>
                )}
                {!simple && withDate && (
                  <Small withLeftMargin={Boolean(author && withAuthor)}>
                    <DateLabel date={new Date(date)} format="LLL" timeZone={SITE_TIME_ZONE} />
                  </Small>
                )}
              </div>
            </Flex>
          </div>
        </ArticleInfo>
      </ArticleContent>
    </ArticleCardContainer>
  );
};
